'use client'

/**
 * PhotoUpload — herbruikbaar upload-component voor profielfoto's
 * (spec 12 §3.1). Drag & drop, klik-om-te-uploaden, preview met camera-
 * overlay, en een verwijder-knop wanneer er al een foto is.
 *
 * Validatie is dubbelop (client-side voor snelle UX-feedback; API valideert
 * opnieuw — die is bron-of-truth):
 *   - mime-types: image/jpeg, image/png, image/webp
 *   - max 5 MB
 *
 * `onUpload`/`onRemove` zijn promises die de aanroeper aanlevert (server-
 * action of fetch). De component beheert het loading-state zelf zodat de UI
 * consistent disabled is tijdens de roundtrip.
 */

import * as React from 'react'
import { Camera, Trash2, Upload } from 'lucide-react'
import { cn } from '../lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { Button } from './button'

const PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
const MAX_PHOTO_SIZE = 5 * 1024 * 1024 // 5 MB

export type PhotoUploadSize = 'sm' | 'md' | 'lg'

const SIZE_CLASSES: Record<PhotoUploadSize, string> = {
  sm: 'h-16 w-16',
  md: 'h-24 w-24',
  lg: 'h-32 w-32',
}

const FALLBACK_TEXT_SIZE: Record<PhotoUploadSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
}

export interface PhotoUploadProps {
  /**
   * URL naar de huidige foto/avatar. `null` toont de fallback met initialen.
   * Mag een API-route zijn (`/api/v1/employees/<id>/photo/thumb`) of een
   * publieke URL — de component rendert via `<img>` zonder validatie.
   */
  currentUrl: string | null
  /** Initialen voor de AvatarFallback wanneer er geen foto is. */
  initials: string
  /**
   * Wordt aangeroepen wanneer de gebruiker een nieuw bestand kiest. De
   * caller doet de fetch/upload; de component beheert alleen UI-state.
   */
  onUpload: (file: File) => Promise<void>
  /** Wordt aangeroepen wanneer de gebruiker op "Verwijderen" klikt. */
  onRemove: () => Promise<void>
  /** Locked-mode: geen upload/verwijder, alleen weergave. */
  disabled?: boolean
  size?: PhotoUploadSize
  /** Optionele callback voor validatie-fouten — zonder fallt het op `alert`. */
  onError?: (message: string) => void
  className?: string
}

export function PhotoUpload({
  currentUrl,
  initials,
  onUpload,
  onRemove,
  disabled = false,
  size = 'lg',
  onError,
  className,
}: PhotoUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [busy, setBusy] = React.useState(false)
  const [dragging, setDragging] = React.useState(false)

  const reportError = React.useCallback(
    (message: string) => {
      if (onError) onError(message)
      else if (typeof window !== 'undefined') window.alert(message)
    },
    [onError],
  )

  const validate = React.useCallback(
    (file: File): boolean => {
      if (!PHOTO_MIME_TYPES.includes(file.type as (typeof PHOTO_MIME_TYPES)[number])) {
        reportError('Alleen JPEG, PNG of WebP toegestaan')
        return false
      }
      if (file.size > MAX_PHOTO_SIZE) {
        reportError('Foto mag maximaal 5 MB zijn')
        return false
      }
      return true
    },
    [reportError],
  )

  const runUpload = React.useCallback(
    async (file: File) => {
      if (!validate(file)) return
      setBusy(true)
      try {
        await onUpload(file)
      } finally {
        setBusy(false)
        if (inputRef.current) inputRef.current.value = ''
      }
    },
    [onUpload, validate],
  )

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) void runUpload(file)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    if (disabled || busy) return
    const file = e.dataTransfer.files?.[0]
    if (file) void runUpload(file)
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || busy) return
    e.preventDefault()
    setDragging(true)
  }

  const onDragLeave = () => setDragging(false)

  const onRemoveClick = async () => {
    if (busy) return
    setBusy(true)
    try {
      await onRemove()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          'group relative rounded-full transition-shadow',
          dragging && 'ring-2 ring-offset-2 ring-[#CCFA78]',
        )}
      >
        <Avatar className={SIZE_CLASSES[size]}>
          {currentUrl ? <AvatarImage src={currentUrl} alt="Profielfoto" /> : null}
          <AvatarFallback
            className={cn(
              'bg-[#F0F1EC] font-semibold text-[#5C5C58]',
              FALLBACK_TEXT_SIZE[size],
            )}
          >
            {initials}
          </AvatarFallback>
        </Avatar>

        {!disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            aria-label="Foto wijzigen"
            className={cn(
              'absolute inset-0 flex items-center justify-center rounded-full',
              'bg-black/40 opacity-0 transition-opacity',
              'hover:opacity-100 focus-visible:opacity-100',
              'disabled:cursor-not-allowed',
            )}
          >
            <Camera className="h-6 w-6 text-white" aria-hidden="true" />
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_MIME_TYPES.join(',')}
        onChange={onFileChange}
        className="hidden"
        disabled={disabled || busy}
      />

      {!disabled && (
        <div className="flex flex-col gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {currentUrl ? 'Wijzigen' : 'Uploaden'}
          </Button>
          {currentUrl && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void onRemoveClick()}
              disabled={busy}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Verwijderen
            </Button>
          )}
          <p className="text-xs text-[#5C5C58]">JPEG, PNG of WebP · max 5&nbsp;MB</p>
        </div>
      )}
    </div>
  )
}
